// Conversation CRUD + SSE streaming client for ai.qada.io.
// Reuses the shared server helpers (API base, CSRF, credentials) so
// there's no parallel auth surface.

import { api, getCsrfToken, request } from "../services/server";

// ── Outreach draft review (step 7) ───────────────────────────────────
//
// These endpoints are the ONLY way the frontend drives the review
// workflow. Approval is a deterministic backend action — never routed
// through the agent. Deliberately does NOT expose a "send" verb: the
// system has no delivery capability.

export function listOutreachDrafts({ workspaceId, status, limit, signal } = {}) {
  const params = new URLSearchParams();
  if (workspaceId != null) params.set("workspace", String(workspaceId));
  if (status) params.set("status", status);
  if (limit != null) params.set("limit", String(limit));
  const q = params.toString();
  return request(`/ai/outreach-drafts/${q ? `?${q}` : ""}`, { signal });
}

export function getOutreachDraft(id, { signal } = {}) {
  return request(`/ai/outreach-drafts/${encodeURIComponent(id)}/`, { signal });
}

export function updateOutreachDraft(id, patch, { signal } = {}) {
  return request(`/ai/outreach-drafts/${encodeURIComponent(id)}/`, {
    method: "PATCH",
    body: patch,
    signal,
  });
}

export function approveOutreachDraft(id, { reviewNotes, signal } = {}) {
  return request(`/ai/outreach-drafts/${encodeURIComponent(id)}/approve/`, {
    method: "POST",
    body: reviewNotes ? { review_notes: reviewNotes } : {},
    signal,
  });
}

export function rejectOutreachDraft(id, { reviewNotes, signal } = {}) {
  return request(`/ai/outreach-drafts/${encodeURIComponent(id)}/reject/`, {
    method: "POST",
    body: reviewNotes ? { review_notes: reviewNotes } : {},
    signal,
  });
}

// Step 8 — HUMAN-APPROVED SEND. This is the ONLY frontend function
// that may initiate outbound email. It's an authenticated POST from
// the browser; the backend enforces authorization + approval integrity
// + content-hash checks before invoking the SMTP provider. The AI
// conversation cannot reach this — the tool registry deliberately
// exposes no such function.
//
// The request body is intentionally empty. Recipient / sender /
// provider are ALL derived server-side from the draft + authenticated
// user. Anything you put in `body` is ignored by the backend.
export function sendOutreachDraft(id, { signal } = {}) {
  return request(`/ai/outreach-drafts/${encodeURIComponent(id)}/send/`, {
    method: "POST",
    body: {},
    signal,
  });
}

// ── Step 9 — inbound buyer replies (read-only unless preparing draft) ──
// None of these functions send email. `prepareReplyToInbound` creates a
// new OutreachDraft in `draft` status; it must go through the step-7/8
// approve + explicit-send flow to reach the buyer.

export function getOutreachThread(draftId, { signal } = {}) {
  return request(
    `/ai/outreach-drafts/${encodeURIComponent(draftId)}/thread/`,
    { signal },
  );
}

export function analyzeInboundMessage(messageId, { signal } = {}) {
  return request(
    `/ai/inbound/messages/${encodeURIComponent(messageId)}/analyze/`,
    { method: "POST", body: {}, signal },
  );
}

export function prepareReplyToInbound(messageId, { signal } = {}) {
  return request(
    `/ai/inbound/messages/${encodeURIComponent(messageId)}/prepare-reply/`,
    { method: "POST", body: {}, signal },
  );
}

// ── Step 11 — deal-level buyer intelligence view ────────────────────
// Aggregated view for one (workspace, acquirer) pair. Workspace is a
// required query param — the backend refuses to guess for
// multi-workspace users. Read-only; no side effects.
export function getBuyerIntelligence(acquirerId, { workspaceId, signal } = {}) {
  const params = new URLSearchParams();
  if (workspaceId != null) params.set("workspace", String(workspaceId));
  const q = params.toString();
  return request(
    `/ai/acquirers/${encodeURIComponent(acquirerId)}/intelligence/${q ? `?${q}` : ""}`,
    { signal },
  );
}

// ── Step 12 — workspace-level Deal Situation layer ─────────────────
// Live aggregate (no persistence) + append-only snapshot log. All
// three endpoints validate workspace membership server-side; the
// frontend never trusts a workspace id it wasn't handed by /ai/context/.

export function getDealSituationLive(workspaceId, { signal } = {}) {
  return request(
    `/ai/workspaces/${encodeURIComponent(workspaceId)}/deal-situation/`,
    { signal },
  );
}

export function listDealSituationSnapshots(workspaceId, { limit, signal } = {}) {
  const params = new URLSearchParams();
  if (limit != null) params.set("limit", String(limit));
  const q = params.toString();
  return request(
    `/ai/workspaces/${encodeURIComponent(workspaceId)}/deal-situation/snapshots/${q ? `?${q}` : ""}`,
    { signal },
  );
}

// Capture a persisted snapshot. Idempotent by default (backend
// short-circuits when the source pointer hash matches the latest
// stored row); pass { force: true } to always create a new row.
export function captureDealSituationSnapshot(workspaceId, { force, signal } = {}) {
  return request(
    `/ai/workspaces/${encodeURIComponent(workspaceId)}/deal-situation/snapshots/`,
    { method: "POST", body: force ? { force: true } : {}, signal },
  );
}

export function getDealSituationSnapshot(workspaceId, pk, { signal } = {}) {
  return request(
    `/ai/workspaces/${encodeURIComponent(workspaceId)}/deal-situation/snapshots/${encodeURIComponent(pk)}/`,
    { signal },
  );
}

// ── Step 13 — Deal Actions ─────────────────────────────────────────
// Read + human-review surface for DealAction. No `send` function
// exists — Step 13 does NOT ship an outbound-communication verb.
// Preparing a reply still goes through the existing Step 7/8 draft
// review + hash-verified send path.

const _wsBase = (id) =>
  `/ai/workspaces/${encodeURIComponent(id)}/actions`;

export function listDealActions(workspaceId, params = {}, { signal } = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v == null) return;
    if (Array.isArray(v)) v.forEach((x) => q.append(k, String(x)));
    else q.set(k, String(v));
  });
  const s = q.toString();
  return request(`${_wsBase(workspaceId)}/${s ? `?${s}` : ""}`, { signal });
}

export function getDealAction(workspaceId, actionId, { signal } = {}) {
  return request(
    `${_wsBase(workspaceId)}/${encodeURIComponent(actionId)}/`,
    { signal },
  );
}

export function createDealAction(workspaceId, body, { signal } = {}) {
  return request(`${_wsBase(workspaceId)}/`, {
    method: "POST", body, signal,
  });
}

export function updateDealAction(workspaceId, actionId, patch, { signal } = {}) {
  return request(
    `${_wsBase(workspaceId)}/${encodeURIComponent(actionId)}/`,
    { method: "PATCH", body: patch, signal },
  );
}

export function createActionFromSuggestion(workspaceId, body, { signal } = {}) {
  return request(`${_wsBase(workspaceId)}/from-suggestion/`, {
    method: "POST", body, signal,
  });
}

export function createActionFromBuyerIntel(workspaceId, body, { signal } = {}) {
  return request(`${_wsBase(workspaceId)}/from-buyer-intel/`, {
    method: "POST", body, signal,
  });
}

// One thin wrapper per verb. The backend rejects invalid transitions
// with an ActionError → 400 code:invalid_transition, so the UI just
// forwards the button click without pre-validating.
function _verb(workspaceId, actionId, verb, body, signal) {
  return request(
    `${_wsBase(workspaceId)}/${encodeURIComponent(actionId)}/${verb}/`,
    { method: "POST", body: body || {}, signal },
  );
}

export const approveDealAction        = (ws, id, o = {}) => _verb(ws, id, "approve",           o, o.signal);
export const rejectDealAction         = (ws, id, o = {}) => _verb(ws, id, "reject",            o, o.signal);
export const startDealAction          = (ws, id, o = {}) => _verb(ws, id, "start",             o, o.signal);
export const blockDealAction          = (ws, id, o = {}) => _verb(ws, id, "block",             o, o.signal);
export const resolveDealActionBlockers = (ws, id, o = {}) => _verb(ws, id, "resolve-blockers",  o, o.signal);
export const completeDealAction       = (ws, id, o = {}) => _verb(ws, id, "complete",          o, o.signal);
export const cancelDealAction         = (ws, id, o = {}) => _verb(ws, id, "cancel",            o, o.signal);
export const supersedeDealAction      = (ws, id, o = {}) => _verb(ws, id, "supersede",         o, o.signal);
export const sendDealActionForReview  = (ws, id, o = {}) => _verb(ws, id, "send-for-review",   o, o.signal);
export const assignDealAction         = (ws, id, o = {}) => _verb(ws, id, "assign",            o, o.signal);

export function getDealActionHistory(workspaceId, actionId, { signal } = {}) {
  return request(
    `${_wsBase(workspaceId)}/${encodeURIComponent(actionId)}/history/`,
    { signal },
  );
}

export function listDealActionComments(workspaceId, actionId, { signal } = {}) {
  return request(
    `${_wsBase(workspaceId)}/${encodeURIComponent(actionId)}/comments/`,
    { signal },
  );
}

export function addDealActionComment(workspaceId, actionId, body, { signal } = {}) {
  return request(
    `${_wsBase(workspaceId)}/${encodeURIComponent(actionId)}/comments/`,
    { method: "POST", body: { body }, signal },
  );
}

// ── Step 14 — assignment-side in-app notifications ─────────────────
// Two tiny endpoints only: list-my-notifications-in-workspace, and
// mark-one-read. No delete, no update, no bulk operations.

export function listNotifications(
  workspaceId, { unreadOnly, limit, offset, signal } = {},
) {
  const q = new URLSearchParams();
  if (unreadOnly) q.set("unread_only", "1");
  if (limit != null) q.set("limit", String(limit));
  if (offset != null) q.set("offset", String(offset));
  const s = q.toString();
  return request(
    `/ai/workspaces/${encodeURIComponent(workspaceId)}/notifications/${s ? `?${s}` : ""}`,
    { signal },
  );
}

export function markNotificationRead(workspaceId, id, { signal } = {}) {
  return request(
    `/ai/workspaces/${encodeURIComponent(workspaceId)}/notifications/${encodeURIComponent(id)}/read/`,
    { method: "POST", body: {}, signal },
  );
}

// ── Step 16 — AI contract drafts + e-signature ─────────────────────
// AI drafts contracts via the `generate_contract_draft` tool (in the
// conversation flow). The frontend surface below is human-only: list,
// review, edit, approve, send for signature. There is NO api call
// that lets the AI approve or send — those are humans-only endpoints.

const _cBase = (id) =>
  `/ai/workspaces/${encodeURIComponent(id)}/contracts`;

export function listContracts(workspaceId, params = {}, { signal } = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v == null) return;
    if (Array.isArray(v)) v.forEach((x) => q.append(k, String(x)));
    else q.set(k, String(v));
  });
  const s = q.toString();
  return request(`${_cBase(workspaceId)}/${s ? `?${s}` : ""}`, { signal });
}

export function getContract(workspaceId, id, { signal } = {}) {
  return request(
    `${_cBase(workspaceId)}/${encodeURIComponent(id)}/`,
    { signal },
  );
}

export function createContract(workspaceId, body, { signal } = {}) {
  return request(`${_cBase(workspaceId)}/`, {
    method: "POST", body, signal,
  });
}

export function updateContract(workspaceId, id, patch, { signal } = {}) {
  return request(
    `${_cBase(workspaceId)}/${encodeURIComponent(id)}/`,
    { method: "PATCH", body: patch, signal },
  );
}

function _cVerb(workspaceId, id, verb, body, signal) {
  return request(
    `${_cBase(workspaceId)}/${encodeURIComponent(id)}/${verb}/`,
    { method: "POST", body: body || {}, signal },
  );
}

export const submitContractForReview = (ws, id, o = {}) => _cVerb(ws, id, "submit-for-review", o, o.signal);
export const approveContract         = (ws, id, o = {}) => _cVerb(ws, id, "approve",           o, o.signal);
export const rejectContract          = (ws, id, o = {}) => _cVerb(ws, id, "reject",            o, o.signal);
export const cancelContract          = (ws, id, o = {}) => _cVerb(ws, id, "cancel",            o, o.signal);
export const supersedeContract       = (ws, id, o = {}) => _cVerb(ws, id, "supersede",         o, o.signal);

// Send for signature — takes {signer_email, signer_name}. If the
// contract has a linked AcquirerContact, the backend fills these
// from the contact when omitted (but also enforces email match).
export function sendContractForSignature(workspaceId, id, body, { signal } = {}) {
  return _cVerb(workspaceId, id, "send-for-signature", body || {}, signal);
}

export function getContractHistory(workspaceId, id, { signal } = {}) {
  return request(
    `${_cBase(workspaceId)}/${encodeURIComponent(id)}/history/`,
    { signal },
  );
}

export function getContractSignatureStatus(workspaceId, id, { signal } = {}) {
  return request(
    `${_cBase(workspaceId)}/${encodeURIComponent(id)}/signature-status/`,
    { signal },
  );
}

export function contractPdfUrl(workspaceId, id) {
  // Returned as a URL string so <a target="_blank"> or <iframe> can
  // hit the FileResponse-streamed endpoint directly.
  return api(`/ai/workspaces/${encodeURIComponent(workspaceId)}/contracts/${encodeURIComponent(id)}/pdf/`);
}

// ── Säljar-wizard (steg för steg från värdering → outreach) ────────
export function getSellerWizard(workspaceId, { signal } = {}) {
  return request(
    `/ai/workspaces/${encodeURIComponent(workspaceId)}/wizard/`,
    { signal },
  );
}

// ── Onboarding wizard (pre-registration; ai.qada.io) ────────────────
// The lookup + advisor + register calls are anonymous — the wizard
// runs before the user has an account. Coverage is authenticated and
// only reachable after register logs the user in.

export function onboardLookup(orgnr, { signal } = {}) {
  return request("/ai/onboard/lookup/", {
    method: "POST",
    body: { orgnr },
    signal,
  });
}

export function onboardRequestAdvisor(body, { signal } = {}) {
  return request("/ai/onboard/request-advisor/", {
    method: "POST",
    body,
    signal,
  });
}

export function onboardRegister(body, { signal } = {}) {
  return request("/ai/onboard/register/", {
    method: "POST",
    body,
    signal,
  });
}

export function getOnboardingCoverage(workspaceId, { signal } = {}) {
  return request(
    `/ai/onboard/workspaces/${encodeURIComponent(workspaceId)}/coverage/`,
    { signal },
  );
}

// Broker Stripe checkout for the self-serve tier (post-registration).
// Reuses the existing /billing/self-serve/checkout/ endpoint.
export function selfServeCheckout(workspaceSlug, { signal } = {}) {
  return request("/billing/self-serve/checkout/", {
    method: "POST",
    body: { workspace_key: workspaceSlug },
    signal,
  });
}

// ── CRUD (JSON) ─────────────────────────────────────────────────────

export function fetchAIContext({ signal } = {}) {
  return request("/ai/context/", { signal });
}

export function listConversations({ signal } = {}) {
  return request("/ai/conversations/", { signal });
}

export function createConversation({ workspaceId, companyId, title, signal } = {}) {
  const body = {};
  if (workspaceId != null) body.workspace_id = workspaceId;
  if (companyId != null) body.company_id = companyId;
  if (title) body.title = title;
  return request("/ai/conversations/", { method: "POST", body, signal });
}

export function getConversation(id, { signal } = {}) {
  return request(`/ai/conversations/${encodeURIComponent(id)}/`, { signal });
}

export function renameConversation(id, title, { signal } = {}) {
  return request(`/ai/conversations/${encodeURIComponent(id)}/`, {
    method: "PATCH",
    body: { title },
    signal,
  });
}

export function deleteConversation(id, { signal } = {}) {
  return request(`/ai/conversations/${encodeURIComponent(id)}/`, {
    method: "DELETE",
    signal,
  });
}

// ── SSE streaming ───────────────────────────────────────────────────

/**
 * POST the user message + consume the SSE stream.
 *
 * We use fetch() + ReadableStream (not EventSource) because EventSource
 * is GET-only, which prevents putting the user's message content in the
 * request body. Same-origin isn't guaranteed either — we depend on the
 * CORS + credentials setup already configured for api.qada.io.
 *
 * onEvent(eventName, dataObj) — called for each SSE frame:
 *   "user_message"       → {id, content, created_at, ...}
 *   "delta"              → {text}
 *   "assistant_message"  → {id, content, created_at, ...}
 *   "error"              → {detail}
 *   "done"               → {ok}
 */
export async function streamMessage(conversationId, content, { onEvent, signal } = {}) {
  const res = await fetch(api(`/ai/conversations/${encodeURIComponent(conversationId)}/stream/`), {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken() || "",
      Accept: "text/event-stream",
    },
    body: JSON.stringify({ content }),
    signal,
  });

  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      /* non-JSON error body — keep generic detail */
    }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }

  if (!res.body) {
    throw new Error("Streaming inte tillgängligt i denna webbläsare");
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line ("\n\n"). Split, keep
      // the last (possibly incomplete) chunk in the buffer for the
      // next read.
      const parts = buffer.split("\n\n");
      buffer = parts.pop() ?? "";

      for (const part of parts) {
        const frame = _parseSseFrame(part);
        if (!frame) continue;
        try {
          onEvent?.(frame.event, frame.data);
        } catch (err) {
          // Never let a UI callback bubble up and abort the stream —
          // log and continue reading.
          // eslint-disable-next-line no-console
          console.error("streamMessage onEvent handler threw:", err);
        }
      }
    }
    // Flush any trailing bytes into the buffer (rarely populated).
    buffer += decoder.decode();
    const tail = _parseSseFrame(buffer);
    if (tail) onEvent?.(tail.event, tail.data);
  } finally {
    try { reader.releaseLock(); } catch { /* noop */ }
  }
}

function _parseSseFrame(raw) {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  let event = "message";
  const dataLines = [];
  for (const line of trimmed.split("\n")) {
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trim());
    }
    // Silently ignore other SSE fields (id:, retry:) — we don't use them.
  }
  const dataStr = dataLines.join("\n");
  let data = {};
  if (dataStr) {
    try {
      data = JSON.parse(dataStr);
    } catch {
      data = { raw: dataStr };
    }
  }
  return { event, data };
}
